import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Text,
  Box,
  FormControl,
  FormLabel,
  Button,
  VStack,
  Alert,
  AlertDescription,
  AlertIcon,
  useToast,
  Input,
} from '@chakra-ui/react';

import { AttachmentIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import FileUploadCard from 'components/FileUploadCard';

import {
  apiUploadCommercialFile,
  apiGetCommercialFiles,
  apiDeleteCommercialFile,
} from 'api';
import { COMMERCIAL_FILE_TYPES } from 'constants/fileTypes';
import { useAppSelector } from 'hooks';
import type { APIApplicationFile } from 'types/api.types';

interface CommercialApplicationUploadProps {
  isLocked?: boolean;
}

const CommercialApplicationUpload = ({
  isLocked = false,
}: CommercialApplicationUploadProps) => {
  const { uuid } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  const submissionType = useAppSelector(
    (state) => state.commercialForm.data.submission_information.submission_type,
  );

  const { data: files = [] } = useQuery({
    queryKey: ['commercial-files', uuid],
    queryFn: () => apiGetCommercialFiles(uuid!),
    refetchOnWindowFocus: false,
  }) as { data: APIApplicationFile[] };

  const applicationFiles = files.filter(
    (file) => file.file_type === COMMERCIAL_FILE_TYPES.APPLICATION,
  );
  const supplementFiles = files.filter(
    (file) => file.file_type === COMMERCIAL_FILE_TYPES.SUPPLEMENT,
  );
  const vehicleScheduleFiles = files.filter(
    (file) => file.file_type === COMMERCIAL_FILE_TYPES.VEHICLE_SCHEDULE,
  );
  const driverScheduleFiles = files.filter(
    (file) => file.file_type === COMMERCIAL_FILE_TYPES.DRIVER_SCHEDULE,
  );
  const combinedApplicationSupplementFiles = files.filter(
    (file) =>
      file.file_type === COMMERCIAL_FILE_TYPES.APPLICATION_SUPPLEMENT_COMBINED,
  );

  const isFleet = submissionType === 'fleet';
  const isIrca = submissionType === 'irca';
  const hasCombinedApplicationSupplement =
    combinedApplicationSupplementFiles.length > 0;
  const hasSeparateUploads =
    applicationFiles.length > 0 || supplementFiles.length > 0;
  const disableSeparateUploads = isIrca && hasCombinedApplicationSupplement;
  const disableCombinedUpload = isIrca && hasSeparateUploads;

  const uploadMutation = useMutation({
    mutationFn: ({ file, fileType }: { file: File; fileType: string }) =>
      apiUploadCommercialFile(uuid!, file, fileType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commercial-files', uuid] });
      toast({
        title: 'Success',
        description: 'File uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Upload failed',
        description:
          error?.response?.data?.error ||
          'Failed to upload file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (fileId: number) => apiDeleteCommercialFile(uuid!, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commercial-files', uuid] });
      toast({
        title: 'File deleted',
        description: 'File removed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: () => {
      toast({
        title: 'Delete failed',
        description: 'Failed to delete file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const handleFileSelect =
    (
      fileType:
        | 'application'
        | 'supplement'
        | 'vehicle_schedule'
        | 'driver_schedule'
        | 'application_and_supplement_combined',
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      uploadMutation.mutate({ file, fileType });

      event.target.value = '';
    };

  const handleRemoveFile = (fileId: number) => {
    deleteMutation.mutate(fileId);
  };

  const infoMessage: {
    body: string;
    additionalInfo?: string;
    note?: string;
    important?: string;
  } | null =
    submissionType === 'fleet'
      ? {
          body: 'Upload the completed unsigned OAP1 Application, Commercial Vehicle Supplement (CVS), Driver Schedule, and Vehicle Schedule with coverages here. These documents will be included in the PrecisionX signing package.',
          important:
            'Only the CVS generated from PowerBroker may be uploaded for signing in the supplement section. If a carrier requires any additional or market-specific supplements to be signed, those documents may be signed separately using your Xodo Sign account.',
        }
      : submissionType === 'garage'
        ? {
            body: 'Upload the completed unsigned OAP4 Application and Garage Auto Supplement here. These documents will be included in the PrecisionX signing package.',
            note: 'If any additional supplements are required to be signed by the carrier (ie. Used Car Dealer Questionnaire, Towing Supplement, etc.) those may be signed separately using your Xodo sign account',
          }
        : submissionType === 'irca'
          ? {
              body: 'Upload the completed unsigned OAP1 Application and Commercial Vehicle Supplement (CVS) here. These documents will be included in the PrecisionX signing package.',
              additionalInfo:
                'If your OAP1 and CVS were created in Applied and saved as one combined PDF, upload the file once using the “Application + Commercial Vehicle Supplement” option.',
              important:
                'Only the CVS generated from PowerBroker may be uploaded in this supplement section for signing. If a carrier requires a market-specific CVS, that document may be signed separately using your Xodo Sign account.',
            }
          : null;

  return (
    <Card mb="20px" id="sectionCommercialApplicationUpload">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Application & Supplement Upload
      </Text>

      <VStack spacing={4} align="stretch">
        {infoMessage && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              <Text fontSize="sm">{infoMessage.body}</Text>
              {infoMessage.additionalInfo && (
                <Text fontSize="sm" mt="2">
                  {infoMessage.additionalInfo}
                </Text>
              )}
              {infoMessage.note && (
                <Text fontSize="sm" mt="2">
                  <strong>NOTE:</strong> {infoMessage.note}
                </Text>
              )}
              {infoMessage.important && (
                <Text fontSize="sm" mt="2">
                  <strong>Important:</strong> {infoMessage.important}
                </Text>
              )}
            </AlertDescription>
          </Alert>
        )}

        {isIrca && (
          <FormControl isRequired>
            <FormLabel fontSize="sm" fontWeight="500" ml="0">
              Combined Application &amp; CVS
            </FormLabel>

            <VStack spacing={3} align="stretch">
              {combinedApplicationSupplementFiles.length === 0 && (
                <Box>
                  <Input
                    type="file"
                    onChange={handleFileSelect(
                      'application_and_supplement_combined',
                    )}
                    display="none"
                    id="combined-application-supplement-upload"
                    disabled={
                      uploadMutation.isPending ||
                      isLocked ||
                      disableCombinedUpload
                    }
                    accept=".pdf,.eml,.msg"
                  />
                  <label htmlFor="combined-application-supplement-upload">
                    <Button
                      as="span"
                      leftIcon={<AttachmentIcon />}
                      colorScheme="blue"
                      variant="outline"
                      size="md"
                      isLoading={uploadMutation.isPending}
                      loadingText="Uploading..."
                      cursor="pointer"
                      width="full"
                      maxW="300px"
                      isDisabled={isLocked || disableCombinedUpload}
                    >
                      Choose File
                    </Button>
                  </label>
                </Box>
              )}

              {combinedApplicationSupplementFiles.map((file) => (
                <FileUploadCard
                  key={file.id}
                  file={file}
                  badgeText="Application + CVS"
                  badgeColorScheme="blue"
                  onDelete={handleRemoveFile}
                  isDeleting={deleteMutation.isPending}
                  isLocked={isLocked}
                />
              ))}
            </VStack>
          </FormControl>
        )}

        <FormControl isRequired={!disableSeparateUploads}>
          <FormLabel fontSize="sm" fontWeight="500" ml="0">
            Application
          </FormLabel>

          <VStack spacing={3} align="stretch">
            {applicationFiles.length === 0 && (
              <Box>
                <Input
                  type="file"
                  onChange={handleFileSelect('application')}
                  display="none"
                  id="application-upload"
                  disabled={
                    uploadMutation.isPending ||
                    isLocked ||
                    disableSeparateUploads
                  }
                  accept=".pdf,.eml,.msg"
                />
                <label htmlFor="application-upload">
                  <Button
                    as="span"
                    leftIcon={<AttachmentIcon />}
                    colorScheme="blue"
                    variant="outline"
                    size="md"
                    isLoading={uploadMutation.isPending}
                    loadingText="Uploading..."
                    cursor="pointer"
                    width="full"
                    maxW="300px"
                    isDisabled={isLocked || disableSeparateUploads}
                  >
                    Choose File
                  </Button>
                </label>
              </Box>
            )}

            {applicationFiles.map((file) => (
              <FileUploadCard
                key={file.id}
                file={file}
                badgeText="Application"
                badgeColorScheme="blue"
                onDelete={handleRemoveFile}
                isDeleting={deleteMutation.isPending}
                isLocked={isLocked}
              />
            ))}
          </VStack>
        </FormControl>

        <FormControl isRequired={!disableSeparateUploads}>
          <FormLabel fontSize="sm" fontWeight="500" ml="0">
            Supplement
          </FormLabel>

          <VStack spacing={3} align="stretch">
            {supplementFiles.length === 0 && (
              <Box>
                <Input
                  type="file"
                  onChange={handleFileSelect('supplement')}
                  display="none"
                  id="supplement-upload"
                  disabled={
                    uploadMutation.isPending ||
                    isLocked ||
                    disableSeparateUploads
                  }
                  accept=".pdf,.eml,.msg"
                />
                <label htmlFor="supplement-upload">
                  <Button
                    as="span"
                    leftIcon={<AttachmentIcon />}
                    colorScheme="blue"
                    variant="outline"
                    size="md"
                    isLoading={uploadMutation.isPending}
                    loadingText="Uploading..."
                    cursor="pointer"
                    width="full"
                    maxW="300px"
                    isDisabled={isLocked || disableSeparateUploads}
                  >
                    Choose File
                  </Button>
                </label>
              </Box>
            )}

            {supplementFiles.map((file) => (
              <FileUploadCard
                key={file.id}
                file={file}
                badgeText="Supplement"
                badgeColorScheme="blue"
                onDelete={handleRemoveFile}
                isDeleting={deleteMutation.isPending}
                isLocked={isLocked}
              />
            ))}
          </VStack>
        </FormControl>

        {isFleet && (
          <>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="500" ml="0">
                Vehicle Schedule
              </FormLabel>

              <VStack spacing={3} align="stretch">
                {vehicleScheduleFiles.length === 0 && (
                  <Box>
                    <Input
                      type="file"
                      onChange={handleFileSelect('vehicle_schedule')}
                      display="none"
                      id="vehicle-schedule-upload"
                      disabled={uploadMutation.isPending || isLocked}
                      accept=".pdf,.eml,.msg"
                    />
                    <label htmlFor="vehicle-schedule-upload">
                      <Button
                        as="span"
                        leftIcon={<AttachmentIcon />}
                        colorScheme="blue"
                        variant="outline"
                        size="md"
                        isLoading={uploadMutation.isPending}
                        loadingText="Uploading..."
                        cursor="pointer"
                        width="full"
                        maxW="300px"
                        isDisabled={isLocked}
                      >
                        Choose File
                      </Button>
                    </label>
                  </Box>
                )}

                {vehicleScheduleFiles.map((file) => (
                  <FileUploadCard
                    key={file.id}
                    file={file}
                    badgeText="Vehicle Schedule"
                    badgeColorScheme="blue"
                    onDelete={handleRemoveFile}
                    isDeleting={deleteMutation.isPending}
                    isLocked={isLocked}
                  />
                ))}
              </VStack>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="500" ml="0">
                Driver Schedule
              </FormLabel>

              <VStack spacing={3} align="stretch">
                {driverScheduleFiles.length === 0 && (
                  <Box>
                    <Input
                      type="file"
                      onChange={handleFileSelect('driver_schedule')}
                      display="none"
                      id="driver-schedule-upload"
                      disabled={uploadMutation.isPending || isLocked}
                      accept=".pdf,.eml,.msg"
                    />
                    <label htmlFor="driver-schedule-upload">
                      <Button
                        as="span"
                        leftIcon={<AttachmentIcon />}
                        colorScheme="blue"
                        variant="outline"
                        size="md"
                        isLoading={uploadMutation.isPending}
                        loadingText="Uploading..."
                        cursor="pointer"
                        width="full"
                        maxW="300px"
                        isDisabled={isLocked}
                      >
                        Choose File
                      </Button>
                    </label>
                  </Box>
                )}

                {driverScheduleFiles.map((file) => (
                  <FileUploadCard
                    key={file.id}
                    file={file}
                    badgeText="Driver Schedule"
                    badgeColorScheme="blue"
                    onDelete={handleRemoveFile}
                    isDeleting={deleteMutation.isPending}
                    isLocked={isLocked}
                  />
                ))}
              </VStack>
            </FormControl>
          </>
        )}
      </VStack>
    </Card>
  );
};

export default CommercialApplicationUpload;
